import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './CompletarTarea.css';

const CompletarTarea = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [tarea, setTarea] = useState(null);
    const [comentario, setComentario] = useState('');
    const [archivo, setArchivo] = useState(null);
    const [tareaCompletada, setTareaCompletada] = useState(false);
    const [mensajesChat, setMensajesChat] = useState([]);
    const [cargandoChat, setCargandoChat] = useState(false);
    const [activeTab, setActiveTab] = useState('responder');
    const [formularioHabilitado, setFormularioHabilitado] = useState(true);
    const [verificandoEstado, setVerificandoEstado] = useState(false);
    const [archivoDescargando, setArchivoDescargando] = useState(null);


    useEffect(() => {
        cargarTarea();
        cargarMensajesChat();
        verificarEstadoFormulario();
    }, [id]);

    useEffect(() => {
        if (!success) return;
        const timer = setTimeout(() => setSuccess(''), 3000);
        return () => clearTimeout(timer);
    }, [success]);

    const cargarTarea = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/tareas/detalles/${id}`);

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Tarea no encontrada o ya fue completada');
                }
                throw new Error('Error al cargar la tarea');
            }

            const result = await response.json();

            if (result.success) {
                setTarea(result.data);
                if (result.data.estado === 'resuelta') {
                    setTareaCompletada(true);
                    setFormularioHabilitado(false);
                } else {
                    setFormularioHabilitado(result.data.formulario_habilitado === 'habilitado');
                }
            } else {
                throw new Error(result.message || 'Error al cargar la tarea');
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const cargarMensajesChat = async () => {
        if (!id) return;

        try {
            setCargandoChat(true);
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/tareas/${id}/mensajes-chat`);

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setMensajesChat(result.data);
                }
            }
        } catch (error) {
            console.error('Error al cargar mensajes del chat:', error);
        } finally {
            setCargandoChat(false);
        }
    };

    const verificarEstadoFormulario = async () => {
        try {
            setVerificandoEstado(true);
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/tareas/estado-formulario/${id}`);

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setFormularioHabilitado(result.data.formulario_habilitado === 'habilitado');
                }
            }
        } catch (error) {
            console.error('Error al verificar estado del formulario:', error);
        } finally {
            setVerificandoEstado(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formularioHabilitado) {
            setError('El formulario está bloqueado. Espera la respuesta del administrador.');
            return;
        }

        if (!comentario.trim()) {
            setError('Por favor, escribe un comentario');
            return;
        }

        if (comentario.trim().length < 10) {
            setError('El comentario debe tener al menos 10 caracteres');
            return;
        }

        if (archivo && archivo.size > 5 * 1024 * 1024) {
            setError('El archivo es demasiado grande. El tamaño máximo es 5MB.');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            setSuccess('');

            const formData = new FormData();
            formData.append('comentario', comentario.trim());
            formData.append('tipo_queja', tarea?.tipo_queja || 'identificada');
            if (archivo) {
                formData.append('archivo', archivo);
            }

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/tareas/completar/${id}`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                setSuccess('¡Respuesta enviada exitosamente!.');
                setComentario('');
                setArchivo(null);
                setFormularioHabilitado(false);
                cargarMensajesChat();
                cargarTarea();

                const fileInput = document.getElementById('archivo');
                if (fileInput) fileInput.value = '';
            } else {
                throw new Error(result.error || 'Error al enviar respuesta');
            }
        } catch (error) {
            setError(`Error: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const fechaLimiteExpirada = () => {
        if (!tarea?.fecha_limite) return false;
        const fechaLimite = new Date(tarea.fecha_limite);
        const ahora = new Date();
        return fechaLimite < ahora;
    };

    const formatearFechaChat = (fecha) => {
        const d = new Date(fecha);
        return d.toLocaleString('es-ES', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const descargarArchivoChat = async (filename) => {
        try {
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/chat/descargar-archivo/${filename}`);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Error ${response.status}: No se puede descargar el archivo`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar archivo:', error);
            setError(error.message || 'Error al descargar archivo');
        }
    };

    const descargarEvidenciaOriginal = async () => {
        if (!tarea?.evidencia_filename) {
            setError('No hay archivo adjunto');
            return;
        }
        try {
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/descargar-evidencia/${tarea.evidencia_filename}`);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Error ${response.status}: No se puede descargar el archivo`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = tarea.evidencia_filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar evidencia:', error);
            setError(error.message || 'Error al descargar evidencia');
        }
    };

    const obtenerIconoArchivo = (filename) => {
        if (!filename) return '📄';
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf': return '📕';
            case 'doc': case 'docx': return '📘';
            case 'jpg': case 'jpeg': case 'png': case 'gif': return '🖼️';
            case 'txt': return '📝';
            case 'xls': case 'xlsx': return '📊';
            case 'zip': case 'rar': return '📦';
            default: return '📄';
        }
    };

    const handleDescarga = async (filename) => {
        try {
            setArchivoDescargando(filename);
            await descargarArchivoChat(filename);
        } finally {
            setArchivoDescargando(null);
        }
    };


    const obtenerClaseMensaje = (mensaje) => {
        if (mensaje.usuario_rol === 'admin') {
            return 'admin-message';
        } else if (mensaje.usuario_rol === 'encargado') {
            return 'encargado-message';
        }
        return '';
    };

    if (loading) {
        return (
            <div className="completar-tarea-container">
                <div className="completar-tarea-card">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                        <p className="mt-3">Cargando información de la tarea...</p>
                    </div>
                </div>
            </div>
        );
    }

    const expirada = fechaLimiteExpirada();

    return (
        <div className="completar-tarea-container">
            <div className="completar-tarea-card">
                <div className="completar-tarea-header">
                    <h1 className="completar-tarea-title">
                        {tarea?.estado === 'resuelta' ? (
                            <>
                                <i className="bi bi-check-circle-fill me-2 text-success"></i>
                                Tarea Completada
                            </>
                        ) : (
                            <>
                                <i className="bi bi-chat-left-text me-2"></i>
                                Responder a la {tarea?.tipo_queja === 'identificada' ? 'Sugerencia' : 'Denuncia'}
                            </>
                        )}
                    </h1>
                    <div className="completar-tarea-subtitle">
                        {tarea?.tipo_queja === 'identificada' ? (
                            <span className="badge bg-info">Sugerencia</span>
                        ) : (
                            <span className="badge bg-warning text-dark">Denuncia</span>
                        )}
                        <span className={`badge ms-2 ${tarea?.estado === 'resuelta' ? 'bg-success' :
                            expirada ? 'bg-danger' : 'bg-primary'}`}>
                            {tarea?.estado === 'resuelta' ? 'Resuelta' :
                                expirada ? 'Expirada' : 'En Proceso'}
                        </span>
                        {!formularioHabilitado && tarea?.estado !== 'resuelta' && (
                            <span className="badge bg-warning ms-2">
                                <i className="bi bi-lock me-1"></i> Formulario Bloqueado
                            </span>
                        )}
                        {formularioHabilitado && tarea?.estado !== 'resuelta' && (
                            <span className="badge bg-success ms-2">
                                <i className="bi bi-unlock me-1"></i> Formulario Habilitado
                            </span>
                        )}
                    </div>
                </div>

                <div className="completar-tarea-info">
                    <div className="nav-section mb-4">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'responder' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('responder')}
                                >
                                    <i className="bi bi-reply me-2"></i>
                                    Responder
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'historial' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('historial')}
                                >
                                    <i className="bi bi-chat-left-text me-2"></i>
                                    Historial de Chat
                                    {mensajesChat.length > 0 && (
                                        <span className="badge bg-secondary ms-2">
                                            {mensajesChat.length}
                                        </span>
                                    )}
                                </button>
                            </li>
                        </ul>
                    </div>

                    <div className="tab-content">
                        {activeTab === 'responder' && (
                            <div className="tab-pane fade show active">
                                {tarea?.estado === 'resuelta' ? (
                                    <div className="alert alert-success">
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        Esta {tarea?.tipo_queja === 'identificada' ? 'sugerencia' : 'denuncia'} ya ha sido marcada como resuelta por el administrador.
                                        <div className="mt-2">
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={() => setActiveTab('historial')}
                                            >
                                                Ver Historial de Conversación
                                            </button>
                                        </div>
                                    </div>
                                ) : expirada ? (
                                    <div className="alert alert-danger">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        La fecha límite para responder ha expirado.
                                        <div className="mt-2">
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={() => setActiveTab('historial')}
                                            >
                                                Ver Historial de Conversación
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="form-section">
                                        <div className="tab-pane fade show active">
                                            <div className="details-section">
                                                {success && (
                                                    <div className="alert alert-success mt-3">
                                                        <i className="bi bi-check-circle-fill me-2"></i>
                                                        {success}
                                                    </div>
                                                )}
                                                <h5 className="mb-3">
                                                    <i className="bi bi-info-circle me-2"></i>
                                                    Detalles de la {tarea?.tipo_queja === 'identificada' ? 'Sugerencia' : 'Denuncia'}
                                                </h5>

                                                <div className="row">
                                                    <div className="col-md-6">

                                                        {tarea?.nombre && (
                                                            <div className="detail-item mb-3">
                                                                <label className="detail-label"><b>Empleado:</b>&nbsp;</label>
                                                                <span className="detail-value">{tarea.nombre}</span>
                                                            </div>
                                                        )}
                                                        {tarea?.correo && (
                                                            <div className="detail-item mb-3">
                                                                <label className="detail-label"><b>Correo:</b>&nbsp;</label>
                                                                <span className="detail-value">{tarea.correo}</span>
                                                            </div>
                                                        )}
                                                        {tarea?.proyecto && (
                                                            <div className="detail-item mb-3">
                                                                <label className="detail-label"><b>Proyecto:</b>&nbsp;</label>
                                                                <span className="detail-value">{tarea.proyecto}</span>
                                                            </div>
                                                        )}
                                                        {tarea?.cargo && (
                                                            <div className="detail-item mb-3">
                                                                <label className="detail-label"><b>Cargo:</b>&nbsp;</label>
                                                                <span className="detail-value">{tarea.cargo}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={tarea?.nombre ? 'col-md-6' : 'col-md-12'}>
                                                        <div className="detail-item mb-3">
                                                            <label className="detail-label"><b>Fecha de creación:&nbsp;</b></label>
                                                            <span className="detail-value">
                                                                {tarea?.fecha_creacion ? new Date(tarea.fecha_creacion).toLocaleString('es-ES') : 'N/A'}
                                                            </span>
                                                        </div>
                                                        {tarea?.fecha_limite && (
                                                            <div className="detail-item mb-3">
                                                                <label className="detail-label"><b>Fecha límite:&nbsp;</b></label>
                                                                <span className={`detail-value ${expirada ? 'text-danger' : ''}`}>
                                                                    {new Date(tarea.fecha_limite).toLocaleString('es-ES')}
                                                                    {expirada && ' (Expirada)'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <hr />

                                                <div className="detail-item mb-3">
                                                    <label className="detail-label">Descripción:</label>
                                                    <div className="detail-value bg-light p-3 rounded">
                                                        {tarea?.descripcion}
                                                    </div>
                                                </div>

                                                {tarea?.evidencia_filename && (
                                                    <div className="detail-item mb-3">
                                                        <label className="detail-label">Evidencia original:</label>
                                                        <div className="detail-value">
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={descargarEvidenciaOriginal}
                                                            >
                                                                <i className="bi bi-download me-1"></i>
                                                                {obtenerIconoArchivo(tarea.evidencia_filename)} {tarea.evidencia_filename}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <form onSubmit={handleSubmit}>
                                            <div className="form-group">
                                                <label htmlFor="comentario" className="form-label">
                                                    <i className="bi bi-chat-left-text me-2"></i>
                                                    Tu respuesta
                                                    <span className="text-danger"> *</span>
                                                </label>
                                                <textarea
                                                    id="comentario"
                                                    className="form-control"
                                                    rows="6"
                                                    value={comentario}
                                                    onChange={(e) => setComentario(e.target.value)}
                                                    placeholder={formularioHabilitado
                                                        ? "Describe cómo resolviste el problema o qué acciones tomaste. Puedes adjuntar archivos de evidencia si es necesario."
                                                        : "Formulario bloqueado. Espera la respuesta del administrador."}
                                                    required
                                                    disabled={submitting || !formularioHabilitado}
                                                />
                                                <div className="d-flex justify-content-between mt-1">
                                                    <small className="text-muted">
                                                        Mínimo 10 caracteres
                                                    </small>
                                                    <small className={`${comentario.length < 10 ? 'text-danger' : 'text-success'}`}>
                                                        {comentario.length} caracteres
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="form-group mt-4">
                                                <label htmlFor="archivo" className="form-label">
                                                    <i className="bi bi-paperclip me-2"></i>
                                                    Archivo adjunto (opcional, máximo 5MB)
                                                </label>
                                                <input
                                                    type="file"
                                                    id="archivo"
                                                    className="form-control"
                                                    onChange={(e) => setArchivo(e.target.files[0])}
                                                    accept="*"
                                                    disabled={submitting || !formularioHabilitado}
                                                />
                                                <small className="text-muted">
                                                    Puedes adjuntar cualquier tipo de archivo (PDF, DOC, imágenes, etc.)
                                                </small>
                                                {archivo && (
                                                    <div className="alert alert-light mt-2">
                                                        <i className="bi bi-file-earmark me-2"></i>
                                                        Archivo seleccionado: {archivo.name}
                                                        <br />
                                                        <small>Tamaño: {(archivo.size / 1024 / 1024).toFixed(2)} MB</small>
                                                    </div>
                                                )}
                                            </div>

                                            {error && (
                                                <div className="alert alert-danger mt-3">
                                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                                    {error}
                                                </div>
                                            )}

                                            {formularioHabilitado ? (
                                                <div className="form-actions mt-4">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary btn-lg"
                                                        disabled={submitting || !comentario.trim() || comentario.trim().length < 10}
                                                    >
                                                        {submitting ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                Enviando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-send me-2"></i>
                                                                Enviar respuesta
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="alert alert-warning mb-4">
                                                    <i className="bi bi-lock me-2"></i>
                                                    <strong>Formulario bloqueado</strong>
                                                    <p className="mb-0 mt-2">
                                                        Espera la respuesta del administrador antes de poder enviar otra respuesta.
                                                        Revisa el historial de chat para ver los últimos mensajes.
                                                    </p>
                                                </div>
                                            )}
                                        </form>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'historial' && (
                            <div className="tab-pane fade show active">
                                <div className="chat-history-section">
                                    <h5 className="mb-3">
                                        <i className="bi bi-chat-left-text me-2"></i>
                                        Historial de la Conversación
                                    </h5>

                                    {cargandoChat ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border spinner-border-sm"></div>
                                            <p className="mt-2">Cargando mensajes...</p>
                                        </div>
                                    ) : mensajesChat.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            <i className="bi bi-chat-left display-4"></i>
                                            <p className="mt-3">No hay mensajes en el chat aún</p>
                                        </div>
                                    ) : (
                                        <div className="chat-messages-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            {mensajesChat.map((msg, index) => (
                                                <div key={index} className={`chat-message ${obtenerClaseMensaje(msg)} mb-4`}>
                                                    <div className="message-header d-flex justify-content-between align-items-center mb-2">
                                                        <div className="d-flex align-items-center">
                                                            <div>
                                                                <strong className="message-sender">{msg.usuario_nombre}</strong>
                                                            </div>
                                                        </div>
                                                        <small className="message-time text-muted">
                                                            {formatearFechaChat(msg.fecha_creacion)}
                                                        </small>
                                                    </div>

                                                    <div className="message-content p-3 rounded" style={{ backgroundColor: '#f4f4f4' }}>
                                                        <p className="mb-0">{msg.mensaje}</p>

                                                        {(msg.archivo_filename || msg.fecha_limite) && (
                                                            <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">

                                                                {msg.archivo_filename && (
                                                                    <div className="message-attachment">
                                                                        <button
                                                                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                                                                            onClick={() => handleDescarga(msg.archivo_filename)}
                                                                            disabled={archivoDescargando === msg.archivo_filename}
                                                                        >
                                                                            {archivoDescargando === msg.archivo_filename ? (
                                                                                <>
                                                                                    <span
                                                                                        className="spinner-border spinner-border-sm"
                                                                                        role="status"
                                                                                        aria-hidden="true"
                                                                                    ></span>
                                                                                    Descargando...
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <i className="bi bi-download"></i>
                                                                                    {obtenerIconoArchivo(msg.archivo_filename)} {msg.archivo_filename}
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                )}


                                                                {msg.fecha_limite && (
                                                                    <div className="message-deadline">
                                                                        <span className="badge bg-warning text-dark">
                                                                            <i className="bi bi-clock me-1"></i>
                                                                            Fecha límite: {new Date(msg.fecha_limite).toLocaleDateString('es-ES')}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                            </div>
                                                        )}


                                                        {msg.estado_formulario === 'bloqueado' && msg.usuario_rol === 'encargado' && (
                                                            <div className="message-status mt-2">
                                                                <span className="badge bg-warning">
                                                                    <i className="bi bi-lock me-1"></i>
                                                                    Formulario bloqueado hasta respuesta del administrador
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompletarTarea;